import {
  FormEvent,
  ForwardedRef,
  Fragment,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useMatches, useNavigate } from 'react-router-dom';
import classNames from 'classnames/bind';
import styles from './TopArea.module.css';

import {
  searchApiSlice,
  useDelAllSearchHistoryMutation,
  useDelSearchHistoryMutation,
} from '../../services/search';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import useModal from '../../hooks/useModal';
import { useSlice } from '../../hooks/useSlice';
import { usePage } from '../../hooks/usePage';

import { selectMenu } from '../../features/infoSlice';
import { searchInpValSet, searchWordSet } from '../../features/searchSlice';

import { Options } from '../../types/library';
import Information from '../Information';
import Button from '../Button';
import Dropdown from '../Dropdown';
import { formatDate } from '../../utils/date';
import { isOfType } from '../../utils/typeGuard';
import {
  DELETE,
  DELETE_AFTER_30_DAYS,
  DELETE_ALL,
  DRIVE,
  EDIT,
  EMPTY_TRASH,
  PREV,
  RECENT_SEARCH_TERMS,
  SEARCH,
  SEARCH_INPUT_PLACEHOLDER,
  SEPARATOR,
  SETTINGS,
  CANCEL,
  ADD_ACTION,
} from '../../constants';
import { selectModalById } from '../../features/modalSlice';
import { isMobile } from '../../utils/util';
import { driveSharedToggled } from '../../features/driveSlice';
import { TooltipWrapper } from '../Tooltip';
import Logo from '../Logo';

interface TopAreaProps {
  title: string;
  className?: string;
  listLen?: number;
}

interface Props {
  [key: string]: {
    name: string;
    type: string;
    id: string;
    uploadTypes?: string[];
  };
}

interface Keyword {
  seq: number;
  search: string;
  date: string;
}

export default forwardRef(function TopArea(
  { title, className, listLen }: TopAreaProps,
  searchInputRef: ForwardedRef<HTMLInputElement>
) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const menu = useAppSelector(selectMenu);
  const { selectOptions, editModeToggled, itemSelected } = useSlice();
  const { sortType, sortOrder, isEditMode }: Options =
    useAppSelector(selectOptions);
  const { pageChanged } = usePage();

  const [getPublicList] = searchApiSlice.useLazyGetPublicListQuery();
  const [getSearchHistory] = searchApiSlice.useLazyGetSearchHistoryQuery();
  const [delAllSearchHistory] = useDelAllSearchHistoryMutation();
  const [delSearchHistory] = useDelSearchHistoryMutation();
  const { toggleModal } = useModal();
  const [isUploadDropdownOpen, setIsUploadDropdownOpen] = useState(false);
  const [isShowKeyword, setIsShowKeyword] = useState(false);
  const [history, setHistory] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [folderPathWidth, setFolderPathWidth] = useState(0);
  const [isShowPathBtn, setIsShowPathBtn] = useState(false);
  const [lastPathWidth, setLastPathWidth] = useState(0);
  const [isShowSearchInput, setIsShowSearchInput] = useState(false);
  const buttonRef = useRef(null);
  const recentAreaRef = useRef<HTMLDivElement>(null);
  const folderPathRef = useRef<HTMLDivElement>(null);
  const firstPathRef = useRef<HTMLSpanElement>(null);
  const secondPathRef = useRef<HTMLSpanElement>(null);

  const matches = useMatches();
  const crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))
    .map((match: any) =>
      match?.handle?.crumb(
        match.pathname
          .split('/home/settings/')
          .filter((path: string) => path !== '')
      )
    );

  const location = useLocation();
  const folderId = location?.state?.folderId;
  const folderName = location?.state?.folderName;
  const navigate = useNavigate();
  const uploadMydriveProgressEntity = useAppSelector((state) =>
    selectModalById(state, 'upload-mydrive-progress')
  );

  const propsByMenu: Props = {
    main: { name: ADD_ACTION, type: 'file', id: 'main_upload' },
    library: {
      name: ADD_ACTION,
      type: 'file',
      id: 'library_upload',
      uploadTypes: ['file'],
    },
    share: {
      name: ADD_ACTION,
      type: 'alert',
      id: 'linkRegistration',
      uploadTypes: ['link'],
    },
    mydrive: {
      name: ADD_ACTION,
      type: 'file',
      id: 'dataRegistration',
      uploadTypes: ['dataFile'],
    },
    search: { name: SEARCH, type: 'file', id: 'search' },
    trash: { name: EMPTY_TRASH, type: 'file', id: 'delete' },
    settings: { name: SETTINGS, type: '', id: '' },
  };

  const props = propsByMenu[menu];

  const Label = props && (
    <>
      <i className={cx('svg_i', menu)}></i>
    </>
  );

  const clickUploadButton = () => {
    setIsUploadDropdownOpen(!isUploadDropdownOpen);
  };

  const openModal = (
    name: string,
    id: string,
    type: string,
    initialButtonDisabled: boolean
  ) => {
    toggleModal({
      name,
      id,
      type,
      initialButtonDisabled,
    });
  };

  const uploadHandler = (name: string) => {
    switch (name) {
      case 'link':
        openModal('linkRegistration', 'linkRegistration', 'alert', true);
        break;
      case 'web':
        toggleModal({
          name: 'dataRegistration',
          id: 'dataRegistration',
          type: 'alert',
          initialButtonDisabled: true,
        });
        break;
      default:
        break;
    }
  };

  const showHistory = async () => {
    try {
      await getSearchHistory()
        .unwrap()
        .then(({ data }) => {
          setHistory(data);
          data.length && setIsShowKeyword(true);
        });
    } catch (error) {
      console.log('Fail to getSearchHistory: ', error);
    }
  };

  const callGetPublicList = async (word?: string) => {
    try {
      await getPublicList({
        page: 0,
        search: word ?? keyword,
        sortBy: sortType,
        sortDir: sortOrder,
      }).unwrap();
      dispatch(pageChanged(0));
    } catch (error) {
      console.log('Fail to getPublicList: ', error);
    }
  };

  const submitKeyword = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    callGetPublicList();
    dispatch(searchWordSet(keyword));
    typeof searchInputRef !== 'function' && searchInputRef?.current?.blur();
  };

  const selectKeyword = (word: string) => {
    setKeyword(word);
    callGetPublicList(word);
    setIsShowKeyword(false);
    dispatch(searchWordSet(word));
  };

  const resetKeyword = () => {
    if (!keyword) {
      setIsShowSearchInput(false);
    }
    selectKeyword('');
  };

  const delAllHistory = async () => {
    try {
      const res = await delAllSearchHistory().unwrap();
      setHistory(res.data);
      setIsShowKeyword(false);
    } catch (error) {
      console.log('Fail to delete search history', error);
    }
  };

  const delHistory = async (seq: number) => {
    try {
      const res = await delSearchHistory({ seq }).unwrap();
      setHistory(res.data);
      !res.data.length && setIsShowKeyword(false);
    } catch (error) {
      console.log('Fail to delete search history', error);
    }
  };

  const closeEditMode = () => {
    dispatch(editModeToggled(false));
    dispatch(itemSelected({ ids: [], item: null }));
    menu === 'mydrive' && dispatch(driveSharedToggled(true));
  };

  const showSearchInput = () => {
    setIsShowSearchInput(true);
  };

  useEffect(() => {
    if (isMobile && isShowSearchInput) {
      isOfType(searchInputRef, 'current') && searchInputRef.current?.focus();
    }
  }, [isShowSearchInput]);

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (
        isOfType<HTMLElement>(target, 'nodeName') &&
        !recentAreaRef.current?.contains(target) &&
        typeof searchInputRef !== 'function' &&
        !searchInputRef?.current?.contains(target)
      ) {
        setIsShowKeyword(false);
        if ((target as HTMLButtonElement).type === 'submit') target.focus();
      }
    };
    isShowKeyword && window.addEventListener('click', clickHandler);

    dispatch(searchInpValSet(keyword));

    return () => {
      window.removeEventListener('click', clickHandler);
    };
  }, [isShowKeyword, history, keyword]);

  useEffect(() => {
    const EDIT_ACTIVE_BOX_WIDTH = 156;
    const folderPathRefWidth = folderPathRef.current?.clientWidth;
    const folderPathW = folderPathRefWidth
      ? folderPathRefWidth - (isEditMode ? EDIT_ACTIVE_BOX_WIDTH : 0)
      : 0;
    const firstPathWidth = firstPathRef.current?.clientWidth ?? 0;
    const secondPathWidth = secondPathRef.current?.clientWidth ?? 0;

    setFolderPathWidth(folderPathW);
    setIsShowPathBtn(folderPathWidth - firstPathWidth < secondPathWidth);
  }, [
    folderPathRef.current,
    firstPathRef.current,
    secondPathRef.current,
    isEditMode,
  ]);

  useEffect(() => {
    const firstPathWidth = firstPathRef.current?.clientWidth ?? 0;
    setLastPathWidth(folderPathWidth - firstPathWidth);
  }, [firstPathRef.current, isShowPathBtn, isEditMode]);

  useEffect(() => {
    const firstPathWidth = firstPathRef.current?.clientWidth ?? 0;
    const secondPathWidth = secondPathRef.current?.clientWidth ?? 0;

    setIsShowPathBtn(folderPathWidth - firstPathWidth < secondPathWidth);
    setLastPathWidth(folderPathWidth - firstPathWidth);
  }, [folderPathWidth]);

  return (
    <>
      <div
        className={cx('top_area', className, menu, { editMode: isEditMode })}
      >
        {isMobile && <Logo wrapperClasses={['logo_area', menu]} />}
        {crumbs.length ? (
          crumbs.map((crumb, i) => <Fragment key={i}>{crumb}</Fragment>)
        ) : (
          <>
            {!!folderId ? (
              <div className={cx('location')} ref={folderPathRef}>
                <span className={cx('part')} ref={firstPathRef}>
                  <Button
                    name={isShowPathBtn ? '' : DRIVE}
                    classnames={['root', isShowPathBtn ? 'icon_more' : '']}
                    handleClick={() => navigate('./')}
                  />
                  <i className={cx('icon_chevron')}>
                    <span className={cx('blind')}>{SEPARATOR}</span>
                  </i>
                </span>
                <span
                  className={cx('part')}
                  ref={secondPathRef}
                  style={{
                    width: isShowPathBtn ? lastPathWidth || '100%' : 'auto',
                  }}
                >
                  <span title={folderName}>{folderName}</span>
                </span>
              </div>
            ) : (
              <>
                {!isShowSearchInput && (
                  <h1 className={cx({ blind: menu === 'main' })}>
                    {title}
                    {isEditMode && ` ${EDIT}`}
                  </h1>
                )}
                {isMobile && isEditMode && (
                  <Button
                    name={PREV}
                    isBlindName={true}
                    handleClick={closeEditMode}
                  >
                    <i className={cx('svg_i')}></i>
                  </Button>
                )}
              </>
            )}
          </>
        )}
        {!isMobile &&
          (menu === 'library' || menu === 'share' || menu === 'mydrive') && (
            <TooltipWrapper
              content={props.name}
              onClick={clickUploadButton}
              externalRef={buttonRef}
            >
              {(tooltipProps) => (
                <Button
                  name={props.name}
                  classnames={[
                    'book_upload',
                    uploadMydriveProgressEntity?.isOpen ? 'is--disabled' : '',
                  ]}
                  isBlindName={true}
                  tooltipProps={tooltipProps}
                >
                  {Label}
                </Button>
              )}
            </TooltipWrapper>
          )}
        <Dropdown
          name="dataUpload"
          classnames={['type_upload_drive']}
          list={props?.uploadTypes ?? []}
          clickHandlers={[uploadHandler]}
          isOpen={
            uploadMydriveProgressEntity?.isOpen ? false : isUploadDropdownOpen
          }
          button={buttonRef}
          backdropHandler={() => setIsUploadDropdownOpen(false)}
        />
        {menu === 'search' && (
          <div className={cx('top_search', { active: isShowSearchInput })}>
            <form role="search" onSubmit={submitKeyword}>
              <div className={cx('search_area')}>
                <span className={cx('blind')}>{SEARCH}</span>
                {isMobile && !isShowSearchInput ? null : (
                  <input
                    type="text"
                    className={cx('search_t')}
                    placeholder={SEARCH_INPUT_PLACEHOLDER}
                    onFocus={showHistory}
                    onChange={({ target }) => setKeyword(target.value)}
                    value={keyword}
                    ref={searchInputRef}
                  />
                )}
                {(keyword || (isMobile && isShowSearchInput)) && (
                  <button
                    type="button"
                    className={cx('btn', 'ico_cancel')}
                    onClick={resetKeyword}
                  >
                    <span className={cx('blind')}>
                      {SEARCH} {CANCEL}
                    </span>
                  </button>
                )}
                <button
                  type={isMobile && !isShowSearchInput ? 'button' : 'submit'}
                  className={cx('btn', 'ico_search')}
                  {...(isMobile &&
                    !isShowSearchInput && {
                      onClick: showSearchInput,
                    })}
                >
                  <span className={cx('blind')}>{SEARCH}</span>
                </button>
              </div>

              {isShowKeyword && (
                <div className={cx('recent_area')} ref={recentAreaRef}>
                  <span className={cx('top')}>
                    <span className={cx('tit')}>{RECENT_SEARCH_TERMS}</span>
                    <button
                      type="button"
                      className={cx('btn', 'skyt', 'delete_recentall')}
                      onClick={delAllHistory}
                    >
                      <span>{DELETE_ALL}</span>
                    </button>
                  </span>

                  <div className={cx('recent_keyword')}>
                    <ul>
                      {history.map((data: Keyword, i) => (
                        <li key={data.seq}>
                          <span className={cx('keyword')}>
                            <span
                              className={cx('text_clip')}
                              onClick={() => selectKeyword(data.search)}
                            >
                              {data.search}
                            </span>
                            <span>{formatDate(data.date, 2)}</span>
                          </span>
                          <button
                            type="button"
                            className={cx('btn', 'delete_recentkey')}
                            onClick={() => delHistory(data.seq)}
                          >
                            {DELETE}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </form>
          </div>
        )}
        {menu === 'trash' && !isMobile && (
          <div className={cx('clean_area')}>
            <span>{DELETE_AFTER_30_DAYS}</span>
            <span
              role="button"
              className={cx('btn', 'skyt', 'clean_trash', {
                'is--disabled': !listLen,
              })}
              onClick={() =>
                openModal(
                  'confirmEmptyTrash',
                  'confirmEmptyTrash',
                  'confirm',
                  false
                )
              }
            >
              {EMPTY_TRASH}
            </span>
          </div>
        )}
      </div>
      <Information />
    </>
  );
});
